<script>
  import { onMount } from "svelte";
  import {
    Page,
    Card,
    CardHeader,
    CardContent,
    Block,
    Button,
  } from "framework7-svelte";
  import { BASE_URL } from "../services/service.config";
  import TradingNavbar from "../components/TradingNavbar.svelte";

  let holdings = [];
  let sips = [];
  let isLoading = false;
  let errorMessage = null;

  async function loadPortfolioData() {
    isLoading = true;
    errorMessage = null;
    try {
      // 1. Fetch SIPs
      const sipsRes = await fetch(`${BASE_URL}/api/v1/mutual-funds/sips`);
      if (sipsRes.ok) {
        const sipsJson = await sipsRes.json();
        const sipsList = Array.isArray(sipsJson?.data) ? sipsJson.data : (Array.isArray(sipsJson) ? sipsJson : []);
        sips = sipsList.map(s => ({
          client: s.client_code || s.client || "-",
          scheme: s.scheme_name || s.SCHEME || s.scheme || "-",
          amount: Number(s.amount || s.OrderVal || 0),
          frequency: s.frequency || s.FrequencyType || "MONTHLY",
          nextDate: s.startdate || s.nextDate || "-",
          status: s.status || "ACTIVE",
        }));
      }

      // 2. Fetch Holdings
      const hRes = await fetch(`${BASE_URL}/api/v1/mutual-funds/holdings`);
      if (hRes.ok) {
        const hJson = await hRes.json();
        const hList = Array.isArray(hJson?.data) ? hJson.data : (Array.isArray(hJson) ? hJson : []);
        holdings = hList.map(h => ({
          client: h.client_code || h.client || "-",
          scheme: h.SCHEME || h.scheme || "-",
          folio: h.FolioNo || h.folio || "-",
          units: Number(h.quantity || h.units || 0),
          nav: Number(h.nav || 0),
          currentValue: Number(h.OrderVal || h.currentValue || 0),
        }));
      }
    } catch (err) {
      console.error("Error loading mutual fund data:", err);
      holdings = [];
      sips = [];
      errorMessage = err.message || "Failed to load mutual funds data";
    } finally {
      isLoading = false;
    }
  }

  onMount(() => {
    loadPortfolioData();
  });
</script>

<Page name="mutual-fund-portfolio">
  <TradingNavbar title="Mutual Funds & SIP Portfolio Monitor" />

  <Block>
    {#if errorMessage}
      <Card class="bg-color-red text-color-white margin-bottom">
        <CardContent>
          <div style="display: flex; align-items: center; justify-content: space-between;">
            <div>
              <strong>⚠️ Mutual Funds API Error</strong>
              <div style="font-size: 13px; margin-top: 4px;">{errorMessage}</div>
            </div>
            <Button fill small color="white" text-color="red" onClick={loadPortfolioData}>
              Retry
            </Button>
          </div>
        </CardContent>
      </Card>
    {/if}

    <Card class="data-table">
      <CardHeader>Active SIP & XSIP Mandate Schedules</CardHeader>
      <CardContent padding={false}>
        <table>
          <thead>
            <tr>
              <th class="label-cell">Client</th>
              <th class="label-cell">Scheme</th>
              <th class="numeric-cell">Installment (₹)</th>
              <th class="label-cell">Frequency</th>
              <th class="label-cell">Next Due Date</th>
              <th class="label-cell">Status</th>
            </tr>
          </thead>
          <tbody>
            {#if sips.length === 0}
              <tr>
                <td colspan="6" style="text-align: center; padding: 24px; color: #94a3b8;">
                  {isLoading ? "Loading SIP mandates..." : "No active SIP schedules found"}
                </td>
              </tr>
            {:else}
              {#each sips as sip}
                <tr>
                  <td class="label-cell">{sip.client}</td>
                  <td class="label-cell font-bold">{sip.scheme}</td>
                  <td class="numeric-cell">₹{sip.amount.toLocaleString('en-IN')}</td>
                  <td class="label-cell">{sip.frequency}</td>
                  <td class="label-cell">{sip.nextDate}</td>
                  <td class="label-cell"><span class="badge color-green">{sip.status}</span></td>
                </tr>
              {/each}
            {/if}
          </tbody>
        </table>
      </CardContent>
    </Card>

    <Card class="data-table">
      <CardHeader>Client Mutual Fund Holdings</CardHeader>
      <CardContent padding={false}>
        <table>
          <thead>
            <tr>
              <th class="label-cell">Client</th>
              <th class="label-cell">Scheme</th>
              <th class="label-cell">Folio No</th>
              <th class="numeric-cell">Units</th>
              <th class="numeric-cell">NAV (₹)</th>
              <th class="numeric-cell">Current Value (₹)</th>
            </tr>
          </thead>
          <tbody>
            {#if holdings.length === 0}
              <tr>
                <td colspan="6" style="text-align: center; padding: 24px; color: #94a3b8;">
                  {isLoading ? "Loading client holdings..." : "No mutual fund holdings found"}
                </td>
              </tr>
            {:else}
              {#each holdings as h}
                <tr>
                  <td class="label-cell">{h.client}</td>
                  <td class="label-cell font-bold">{h.scheme}</td>
                  <td class="label-cell">{h.folio}</td>
                  <td class="numeric-cell">{h.units.toFixed(2)}</td>
                  <td class="numeric-cell">₹{h.nav.toFixed(2)}</td>
                  <td class="numeric-cell font-bold">₹{h.currentValue.toLocaleString('en-IN')}</td>
                </tr>
              {/each}
            {/if}
          </tbody>
        </table>
      </CardContent>
    </Card>
  </Block>
</Page>
